/* Stacked gallery scroll reveal (desktop only; no-op elsewhere).
   The cross-document morph itself lives in snippets/view-transitions-head.liquid —
   it must run inline in <head> so its pagereveal listener is registered before
   the first render; a deferred module here would be too late. */

const stacked = document.querySelector('.product--gallery-stacked');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (
  stacked &&
  !reduceMotion &&
  'IntersectionObserver' in window &&
  window.matchMedia('(min-width: 60em)').matches
) {
  const wrappers = Array.from(
    stacked.querySelectorAll('.product__media .media-wrapper')
  ).slice(1);
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('vt-in');
          io.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  wrappers.forEach((w) => {
    if (w.getBoundingClientRect().top > window.innerHeight) {
      w.classList.add('vt-reveal');
      io.observe(w);
    }
  });
}

/* Mobile gallery counter ("1 / 6") for the product media carousel.
   Flickity toggles .is-selected on cells; a MutationObserver keeps the
   counter in sync without needing access to the carousel instance. */
const mediaContainer = document.querySelector('.product__media-container');

if (mediaContainer && window.matchMedia('(max-width: 60em)').matches) {
  const cells = Array.from(mediaContainer.querySelectorAll('.product__media .media-wrapper'));
  if (cells.length > 1) {
    const counter = document.createElement('div');
    counter.className = 'vt-gallery-counter';
    counter.setAttribute('aria-hidden', 'true');
    mediaContainer.appendChild(counter);

    const update = () => {
      const idx = cells.findIndex((c) => c.classList.contains('is-selected'));
      counter.textContent = (idx === -1 ? 1 : idx + 1) + ' / ' + cells.length;
    };
    update();

    const mo = new MutationObserver(update);
    cells.forEach((c) => mo.observe(c, { attributes: true, attributeFilter: ['class'] }));
  }
}

/* Sticky add-to-bag bar (mobile): appears once the main buy button leaves
   the viewport, hides again when it returns. Price mirrors the main price
   element, which the theme updates on variant change. */
const stickyBar = document.querySelector('[data-sticky-atc]');
const mainSubmit = document.querySelector('.product-form__item--submit');

if (stickyBar && mainSubmit && 'IntersectionObserver' in window) {
  const barObserver = new IntersectionObserver((entries) => {
    const show = !entries[0].isIntersecting;
    stickyBar.classList.toggle('is-visible', show);
    stickyBar.setAttribute('aria-hidden', String(!show));
  });
  barObserver.observe(mainSubmit);

  const mainPrice = document.querySelector('[data-product-price]');
  const barPrice = stickyBar.querySelector('[data-sticky-atc-price]');
  if (mainPrice && barPrice) {
    new MutationObserver(() => {
      barPrice.textContent = mainPrice.textContent.trim();
    }).observe(mainPrice, { childList: true, subtree: true, characterData: true });
  }
}

/* Morph zoom for stacked gallery images (desktop): clicking an image opens a
   full-screen overlay via a same-document view transition — the inline image
   morphs out to full size and back on close. Replaces the theme lightbox for
   the stacked layout only; mobile keeps the theme's tap behavior. */
if (stacked) {
  // Width is checked at click time, not registration time — the page may
  // load at mobile width and be resized to desktop (or vice versa).
  let zoomOverlay = null;
  let zoomSource = null;

  const closeZoom = () => {
    if (!zoomOverlay) return;
    const overlay = zoomOverlay;
    zoomOverlay = null;
    const finishClose = () => {
      overlay.remove();
      document.documentElement.classList.remove('vt-zoom-open');
    };
    if (document.startViewTransition && !reduceMotion && zoomSource) {
      const big = overlay.querySelector('img');
      big.style.viewTransitionName = 'zoom-hero';
      const t = document.startViewTransition(() => {
        big.style.viewTransitionName = '';
        finishClose();
        zoomSource.style.viewTransitionName = 'zoom-hero';
      });
      t.finished.finally(() => {
        if (zoomSource) zoomSource.style.viewTransitionName = '';
        zoomSource = null;
      });
    } else {
      finishClose();
      zoomSource = null;
    }
  };

  const openZoom = (sourceImg) => {
    const src = sourceImg.currentSrc || sourceImg.src;
    const overlay = document.createElement('div');
    overlay.className = 'vt-zoom';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', sourceImg.alt || 'Zoomed product image');
    const big = document.createElement('img');
    big.alt = sourceImg.alt || '';
    // Open with the rendition that is already decoded — a not-yet-loaded
    // hi-res src gives the view transition a blank "new" snapshot, which
    // plays as a reversed/zoom-out animation followed by the image popping
    // in. The hi-res upgrade happens after the transition settles.
    big.src = src;
    const hiRes = src.replace(/([?&])width=\d+/, '$1width=1800');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'vt-zoom__close';
    close.textContent = 'Close';
    overlay.append(big, close);
    overlay.addEventListener('click', closeZoom);
    zoomSource = sourceImg;
    const finishOpen = () => {
      document.body.appendChild(overlay);
      document.documentElement.classList.add('vt-zoom-open');
      zoomOverlay = overlay;
    };
    const upgrade = () => {
      if (hiRes !== src) big.src = hiRes;
    };
    if (document.startViewTransition && !reduceMotion) {
      sourceImg.style.viewTransitionName = 'zoom-hero';
      const t = document.startViewTransition(() => {
        sourceImg.style.viewTransitionName = '';
        finishOpen();
        big.style.viewTransitionName = 'zoom-hero';
      });
      t.finished.finally(() => {
        big.style.viewTransitionName = '';
        upgrade();
      });
    } else {
      finishOpen();
      upgrade();
    }
  };

  document.addEventListener(
    'click',
    (e) => {
      if (zoomOverlay) return;
      if (!window.matchMedia('(min-width: 60em)').matches) return;
      const link = e.target.closest('.product--gallery-stacked .product__media a.media__image');
      if (!link) return;
      const img = link.querySelector('img');
      if (!img) return;
      e.preventDefault();
      e.stopPropagation();
      openZoom(img);
    },
    true
  );

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeZoom();
  });
}
